import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Form, Modal, Badge, Nav } from 'react-bootstrap';
import { Plus, JournalText, Star, StarFill, Eye, Code } from 'react-bootstrap-icons';
import ReactMarkdown from 'react-markdown';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const PersonalNotes = ({ activeProfileId }) => {
    const [personalNotes, setPersonalNotes] = useState([]);
    const [showPersonalNoteModal, setShowPersonalNoteModal] = useState(false);
    const [selectedPersonalNote, setSelectedPersonalNote] = useState(null);
    const [previewMode, setPreviewMode] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        category: '',
        content: '',
        tags: '',
        is_favorite: false
    });

    useEffect(() => {
        if (activeProfileId) {
            fetchPersonalNotes();
        }
    }, [activeProfileId]);

    const fetchPersonalNotes = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/life/profiles/${activeProfileId}/personal-notes`);
            setPersonalNotes(response.data);
        } catch (error) {
            console.error('Error fetching personal notes:', error);
        }
    };

    const handleEditPersonalNote = (note) => {
        console.log('Editing note:', note); // Debug log
        setSelectedPersonalNote(note);
        setFormData({
            title: note.title || '',
            category: note.category || '',
            content: note.content || '',
            tags: note.tags || '',
            is_favorite: note.is_favorite || false
        });
        setShowPersonalNoteModal(true);
    };

    const handleAddPersonalNote = () => {
        setSelectedPersonalNote(null);
        setFormData({
            title: '',
            category: '',
            content: '',
            tags: '',
            is_favorite: false
        });
        setShowPersonalNoteModal(true);
    };

    const handleSubmitPersonalNote = async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        
        // Debug: log the form data
        console.log('Form data:', Object.fromEntries(formData));
        
        try {
            if (selectedPersonalNote) {
                await axios.put(`${API_BASE_URL}/api/life/personal-notes/${selectedPersonalNote.id}`, formData);
            } else {
                await axios.post(`${API_BASE_URL}/api/life/profiles/${activeProfileId}/personal-notes`, formData);
            }
            fetchPersonalNotes();
            setShowPersonalNoteModal(false);
            setSelectedPersonalNote(null);
            e.target.reset();
        } catch (error) {
            console.error('Error saving personal note:', error);
        }
    };

    const handleDeletePersonalNote = async (noteId) => {
        if (window.confirm('Are you sure you want to delete this note?')) {
            try {
                await axios.delete(`${API_BASE_URL}/api/life/personal-notes/${noteId}`);
                fetchPersonalNotes();
            } catch (error) {
                console.error('Error deleting personal note:', error);
            }
        }
    };

    return (
        <div className="personal-notes-container">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h5><JournalText className="me-2" />Personal Notes</h5>
                <Button variant="primary" onClick={handleAddPersonalNote}>
                    <Plus /> Add Note
                </Button>
            </div>
            
            {personalNotes.length === 0 ? (
                <Card className="text-center p-4">
                    <p className="text-muted">No personal notes yet. Start capturing your memories, ideas, and thoughts!</p>
                </Card>
            ) : (
                <Row>
                    {personalNotes.map(note => (
                        <Col md={6} lg={4} key={note.id} className="mb-3">
                            <Card className="h-100">
                                <Card.Body>
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <h6 className="card-title">{note.title}</h6>
                                        {note.is_favorite && <StarFill className="text-warning" />}
                                    </div>
                                    {note.category && (
                                        <Badge bg="secondary" className="mb-2">{note.category}</Badge>
                                    )}
                                    {note.content && (
                                        <div className="card-text text-muted" style={{
                                            display: '-webkit-box',
                                            WebkitLineClamp: 3,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden'
                                        }}>
                                            <ReactMarkdown>{note.content}</ReactMarkdown>
                                        </div>
                                    )}
                                    {note.tags && (
                                        <div className="mb-2">
                                            {note.tags.split(',').map((tag, index) => (
                                                <Badge key={index} bg="light" text="dark" className="me-1">
                                                    #{tag.trim()}
                                                </Badge>
                                            ))}
                                        </div>
                                    )}
                                    <small className="text-muted">
                                        {new Date(note.updated_at).toLocaleDateString()}
                                    </small>
                                </Card.Body>
                                <Card.Footer className="bg-transparent">
                                    <div className="d-flex justify-content-end gap-2">
                                        <Button variant="outline-primary" size="sm" onClick={() => handleEditPersonalNote(note)}>
                                            Edit
                                        </Button>
                                        <Button variant="outline-danger" size="sm" onClick={() => handleDeletePersonalNote(note.id)}>
                                            Delete
                                        </Button>
                                    </div>
                                </Card.Footer>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}

            {/* Add/Edit Personal Note Modal */}
            <Modal show={showPersonalNoteModal} onHide={() => {setShowPersonalNoteModal(false); setSelectedPersonalNote(null);}} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{selectedPersonalNote ? 'Edit Personal Note' : 'Add Personal Note'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmitPersonalNote}>
                        <Form.Group className="mb-3">
                            <Form.Label>Title *</Form.Label>
                            <Form.Control 
                                name="title" 
                                value={formData.title}
                                onChange={(e) => setFormData({...formData, title: e.target.value})}
                                required 
                                placeholder="e.g., Weekend Trip Memory, Project Ideas" 
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Category</Form.Label>
                            <Form.Select 
                                name="category" 
                                value={formData.category}
                                onChange={(e) => setFormData({...formData, category: e.target.value})}
                            >
                                <option value="">Select Category</option>
                                <option value="Memory">Memory</option>
                                <option value="Idea">Idea</option>
                                <option value="Reminder">Reminder</option>
                                <option value="Thought">Thought</option>
                                <option value="Quote">Quote</option>
                                <option value="Recipe">Recipe</option>
                                <option value="Travel">Travel</option>
                                <option value="Other">Other</option>
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Check 
                                name="is_favorite" 
                                type="checkbox" 
                                label="Mark as Favorite" 
                                checked={formData.is_favorite}
                                onChange={(e) => setFormData({...formData, is_favorite: e.target.checked})}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <Form.Label>Content</Form.Label>
                                <Nav variant="pills" className="nav-sm">
                                    <Nav.Item>
                                        <Nav.Link 
                                            active={!previewMode} 
                                            onClick={() => setPreviewMode(false)}
                                            className="py-1 px-2"
                                        >
                                            <Code size={14} /> Edit
                                        </Nav.Link>
                                    </Nav.Item>
                                    <Nav.Item>
                                        <Nav.Link 
                                            active={previewMode} 
                                            onClick={() => setPreviewMode(true)}
                                            className="py-1 px-2"
                                        >
                                            <Eye size={14} /> Preview
                                        </Nav.Link>
                                    </Nav.Item>
                                </Nav>
                            </div>
                            {previewMode ? (
                                <div 
                                    style={{ 
                                        border: '1px solid #ced4da', 
                                        borderRadius: '0.375rem', 
                                        padding: '0.75rem',
                                        minHeight: '150px',
                                        backgroundColor: '#f8f9fa'
                                    }}
                                >
                                    <ReactMarkdown>{formData.content || '*No content to preview*'}</ReactMarkdown>
                                </div>
                            ) : (
                                <Form.Control 
                                    name="content" 
                                    as="textarea" 
                                    rows={6} 
                                    value={formData.content}
                                    onChange={(e) => setFormData({...formData, content: e.target.value})}
                                    placeholder="Write your note content here... (Supports Markdown)" 
                                />
                            )}
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Tags</Form.Label>
                            <Form.Control 
                                name="tags" 
                                value={formData.tags}
                                onChange={(e) => setFormData({...formData, tags: e.target.value})}
                                placeholder="Comma-separated tags (e.g., family, vacation, work)" 
                            />
                        </Form.Group>
                        <div className="d-flex justify-content-end gap-2">
                            <Button variant="secondary" onClick={() => {setShowPersonalNoteModal(false); setSelectedPersonalNote(null);}}>Cancel</Button>
                            <Button variant="primary" type="submit">{selectedPersonalNote ? 'Update Note' : 'Add Note'}</Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default PersonalNotes;
